import { Div, Span, router } from '@codesuma/baseline'
import { BackIcon } from 'components/back-icon'
import { SharePanel } from '../../../components/share-panel'
import { ShareState } from '../../../helpers'
import style from './index.module.css'

const STATE_TITLE: Record<ShareState, string> = {
    'private': 'Private list',
    'shared-by-me': 'Shared with others',
    'shared-with-me': 'Shared with you',
}

// The list page header: back-to-grid, the editable list title, a private/shared
// badge (tap to open Share), an always-visible share button, and a ⋮ menu
// (Rename / Add section / Copy as text / Change color / Change icon / Pin /
// Delete-or-Leave).
export const ListHeader = () => {
    const base = Div()
    base.addClass(style.base)

    // currentColor so it picks up the per-list dark text color set on the page.
    const back = BackIcon('currentColor')
    back.addClass(style.back)
    back.el.onclick = () => router.goto('/')

    // Tap the title (or the menu's Rename) to rename the list inline — same
    // contenteditable pattern the list items use. `current` holds the committed
    // name for revert.
    const title = Div()
    title.addClass(style.title)
    const titleText = Span('List')
    titleText.addClass(style.titleText)
    title.append(titleText)

    let current = 'List'
    const startEdit = () => {
        titleText.el.setAttribute('contenteditable', 'true')
        titleText.addClass(style.editing)
        titleText.el.focus()
        const range = document.createRange()
        range.selectNodeContents(titleText.el)
        const sel = window.getSelection()
        if (sel) { sel.removeAllRanges(); sel.addRange(range) }
    }
    const commitEdit = () => {
        titleText.el.removeAttribute('contenteditable')
        titleText.removeClass(style.editing)
        const value = (titleText.el.textContent || '').trim()
        if (value && value !== current) {
            current = value
            base.emit('rename', { name: value })
        } else {
            titleText.text(current) // discard empties / no-ops
        }
    }
    titleText.el.onclick = startEdit
    titleText.el.addEventListener('blur', commitEdit)
    titleText.el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); titleText.el.blur() }
        if (e.key === 'Escape') { titleText.text(current); titleText.el.blur() }
    })

    // Full-screen share overlay, appended to the body on demand (see frontend
    // pattern). Opened from the badge and the menu's Share option.
    const sharePanel = SharePanel()
    sharePanel.on('close', () => {
        if (sharePanel.el.parentNode) sharePanel.el.parentNode.removeChild(sharePanel.el)
    })
    const openShare = () => {
        document.body.appendChild(sharePanel.el)
        sharePanel.show()
    }

    // Private / shared badge (icon + how many people are on the list) —
    // populated by setShareState once members load. Tap it to open Share.
    const stateTag = Div()
    stateTag.addClass(style.stateTag)
    stateTag.style({ display: 'none' })
    const stateIcon = Div()
    stateIcon.addClass(style.stateIcon)
    const stateCount = Span('')
    stateCount.addClass(style.stateCount)
    stateTag.append(stateIcon, stateCount)
    stateTag.el.onclick = openShare

    // Always-visible share button (person + plus, in the page's text color):
    // sharing is the app's core action, so it gets its own spot instead of
    // hiding in the ⋮ menu (product principle: discoverable without explanation).
    const shareBtn = Div()
    shareBtn.addClass(style.shareBtn)
    const shareIcon = Div()
    shareIcon.addClass(style.shareIcon)
    shareBtn.append(shareIcon)
    shareBtn.el.setAttribute('title', 'Share this list')
    shareBtn.el.onclick = openShare

    // ⋮ button + dropdown menu (Rename / Delete).
    const dots = Div()
    dots.addClass(style.dots)
    const dotsIcon = Div() // tinted to the page foreground via CSS mask
    dotsIcon.addClass(style.dotsIcon)
    dots.append(dotsIcon)

    const menu = Div()
    menu.addClass(style.menu)
    const renameOption = Div('Rename')
    renameOption.addClass(style.menuItem)
    const sectionOption = Div('Add section')
    sectionOption.addClass(style.menuItem)
    const copyOption = Div('Copy as text')
    copyOption.addClass(style.menuItem)
    const colorOption = Div('Change color')
    colorOption.addClass(style.menuItem)
    const iconOption = Div('Change icon')
    iconOption.addClass(style.menuItem)
    const pinOption = Div('Pin to top') // label flips via setPinned below
    pinOption.addClass(style.menuItem)
    const deleteOption = Div('Delete')
    deleteOption.addClass(style.menuItem, style.menuItemDanger)
    menu.append(renameOption, sectionOption, copyOption, colorOption, iconOption, pinOption, deleteOption)

    // Close on outside click while open, no leaked listeners (mirrors list-item).
    let menuOpen = false
    const onDocClick = () => closeMenu()
    const closeMenu = () => {
        menu.removeClass(style.menuOpen)
        menuOpen = false
        document.removeEventListener('click', onDocClick)
    }
    dots.el.onclick = (e) => {
        e.stopPropagation()
        if (menuOpen) return closeMenu()
        menu.addClass(style.menuOpen)
        menuOpen = true
        setTimeout(() => document.addEventListener('click', onDocClick), 0)
    }
    renameOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); startEdit() }
    sectionOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('add-section') }
    copyOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('copy') }
    colorOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('color') }
    iconOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('icon') }
    pinOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('pin') }
    deleteOption.el.onclick = (e) => { e.stopPropagation(); closeMenu(); base.emit('delete') }

    base.append(back, title, stateTag, shareBtn, dots, menu)

    return Object.assign(base, {
        setTitle: (name: string) => { current = name; titleText.text(name) },
        // Menu label reflects the current pin state (explicit words over an icon —
        // product principle). The page sets it on enter and after each toggle.
        setPinned: (on: boolean) => pinOption.text(on ? 'Unpin from top' : 'Pin to top'),
        // Private lists get no badge; both shared directions use the share icon
        // plus how many people are on the list (owner included).
        setShareState: (state: ShareState, memberCount?: number) => {
            // A member can't destroy a list someone shared with them — their
            // "delete" leaves it instead, so the menu must say so.
            deleteOption.text(state === 'shared-with-me' ? 'Leave list' : 'Delete')
            // Once the list is shared, the badge below takes over as the share
            // entry point — hide the chip so there aren't two share buttons.
            shareBtn.style({ display: state === 'private' ? 'flex' : 'none' })
            if (state === 'private') return
            // The icon is a currentColor-tinted CSS mask (see .stateIcon), so it
            // already follows the page's text color — just reveal and label it.
            stateCount.text(typeof memberCount === 'number' && memberCount > 1 ? String(memberCount) : '')
            stateTag.el.setAttribute('title', STATE_TITLE[state])
            stateTag.style({ display: 'inline-flex' })
        },
        // Hide a stale badge until this list's members load (and reset the
        // menu label + share chip carried over from the previous list).
        hideState: () => {
            stateTag.style({ display: 'none' })
            shareBtn.style({ display: 'flex' })
            deleteOption.text('Delete')
        },
    })
}
